import * as React from 'react'

import { Box, Tabs } from '@factorialco/deprecated-design-system'
import {
  PerformanceReviewerStrategiesStrategyEnum,
  PerformanceReviewerStrategiesStrategyEnumValues,
} from 'generated/resources/datatable.graphql'
import {
  PerformanceRatingScaleOption,
  PerformanceScoreConfigurationFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { isEnumValue } from 'modules/core/lib/utils/enums'

import { CompetenciesAssessmentsResponsesPlaceholder } from 'modules/performance_process/components/CompetenciesAssessmentsResponsesPlaceholder'
import { BoxedCallout } from 'modules/performance_process/components/common/BoxedCallout'
import tabsPicker from 'modules/performance_process/components/review/show/helpers/tabsPicker'
import { WeightTag } from 'modules/performance_process/components/score_configuration_weights/WeightTag'
import {
  configurationByStrategy,
  useStrategyWeights,
} from 'modules/performance_process/hooks/useStrategiesScoreConfiguration'
import {
  type QuestionnaireByReviewerStrategy,
  QuestionnaireContent as QuestionnaireContentForStrategies,
  countQuestions,
  employeePotentialScoreForStrategy,
  employeeScoreForStrategy,
  mapQuestion,
  questionsForStrategy,
} from 'modules/performance_process/lib/questionnaireForStrategies'
import { QuestionnaireResponse } from 'modules/questionnaires/components/QuestionnaireResponse'

const calculateTag = (
  questionnaires: QuestionnaireByReviewerStrategy,
  tabId: PerformanceReviewerStrategiesStrategyEnum
): string => {
  const questions = [
    ...questionsForStrategy(questionnaires, tabId),
    ...employeeScoreForStrategy(questionnaires, tabId),
  ]

  questions.push(...employeePotentialScoreForStrategy(questionnaires, tabId))

  return countQuestions(questions).toString()
}

type Props = {
  reviewType: PerformanceReviewerStrategiesStrategyEnum[]
  questionnaires?: QuestionnaireByReviewerStrategy
  scoreScale: PerformanceRatingScaleOption[]
  readonly?: boolean
  isCompetenciesAssessmentsEnabled: boolean
  scoreConfiguration?: PerformanceScoreConfigurationFragment | null
  hasCustomScoreConfigurationEnabled?: boolean
  isJobCatalogTreeEnabled: boolean
}

export const QuestionnairePreview: React.FC<Props> = ({
  reviewType,
  questionnaires,
  readonly = false,
  isCompetenciesAssessmentsEnabled,
  scoreConfiguration,
  scoreScale,
  hasCustomScoreConfigurationEnabled = false,
  isJobCatalogTreeEnabled,
}) => {
  const tabs = tabsPicker(reviewType)
  const [currentTab, setCurrentTab] = React.useState<PerformanceReviewerStrategiesStrategyEnum>(
    tabs[0]?.id || 'self'
  )
  const validCurrentTab: PerformanceReviewerStrategiesStrategyEnum =
    tabs.find((t) => t.id == currentTab)?.id ?? tabs[0]?.id ?? 'self'

  const strategyConfigurations = configurationByStrategy(scoreConfiguration)
  const { isSectionWeightVisible, weightsBySectionId, hasCustomWeights } = useStrategyWeights({
    strategy: validCurrentTab,
    scoreConfiguration,
  })

  const handleTabClick = (id: PerformanceReviewerStrategiesStrategyEnum) => {
    const tab = tabs.find((t) => t.id == id)
    if (tab) {
      setCurrentTab(id)
    }
    return null
  }

  const currentQuestionnaire = questionsForStrategy(questionnaires, currentTab)
  const currentEmployeeScoreQuestionnaire = employeeScoreForStrategy(questionnaires, currentTab)
  const currentEmployeePotentialScoreQuestionnaire = employeePotentialScoreForStrategy(
    questionnaires,
    currentTab
  )

  const competenciesSectionIds: Partial<
    Record<PerformanceReviewerStrategiesStrategyEnum, undefined | string>
  > = {
    self: questionnaires?.competenciesPlaceholderSelfQuestionnaire?.content?.[0]?.uuid,
    manager: questionnaires?.competenciesPlaceholderManagerQuestionnaire?.content?.[0]?.uuid,
  }
  const currentCompetencySectionId = competenciesSectionIds[currentTab]

  const mapContent = (content: QuestionnaireContentForStrategies[]) => {
    return content
      .map((item) => {
        if (item.type === 'section') {
          const section = {
            uuid: item.uuid,
            title: item.sectionTitle || '',
            questions: item.questions.map((q) => ({
              question: mapQuestion(q),
              answer: null,
            })),
          }
          return section
        }

        const firstQuestion = item.questions[0]
        if (!firstQuestion) {
          return null
        }
        return {
          question: mapQuestion(firstQuestion),
          answer: null,
        }
      })
      .filter(Boolean)
  }

  const questionnaireContent = mapContent(currentQuestionnaire)
  const employeeScoreContent = mapContent(currentEmployeeScoreQuestionnaire)
  const employeePotentialScoreContent = mapContent(currentEmployeePotentialScoreQuestionnaire)

  const showCompetencies =
    isCompetenciesAssessmentsEnabled &&
    (currentTab === 'manager' || currentTab === 'self') &&
    questionnaires

  const renderSectionHeader = (sectionId: string) => {
    if (isSectionWeightVisible) {
      return <WeightTag weight={weightsBySectionId[sectionId] ?? 0} weightable />
    }
  }

  const getTabTag = (tab: PerformanceReviewerStrategiesStrategyEnum) => {
    const questionsCount = questionnaires ? calculateTag(questionnaires, tab) : '0'
    const strategyWeight = strategyConfigurations[tab]?.weight || 0
    return hasCustomScoreConfigurationEnabled && hasCustomWeights
      ? `${strategyWeight}%`
      : questionsCount
  }

  return (
    <Box width="full">
      <Tabs
        key={'question-preview-tabs'}
        currentId={validCurrentTab}
        onTabClicked={(id) =>
          isEnumValue(PerformanceReviewerStrategiesStrategyEnumValues, id) && handleTabClick(id)
        }
      >
        {tabs.map((tab) => (
          <Tabs.Tab key={tab.id} id={tab.id} label={tab.label} tag={getTabTag(tab.id)}>
            <Box paddingY="s32" gap="s32">
              <QuestionnaireResponse
                readonly={readonly}
                content={questionnaireContent}
                onAnswer={() => ({})}
                emptyState={showCompetencies ? <></> : undefined}
                renderSectionHeaderAddon={renderSectionHeader}
              />
              {showCompetencies && scoreScale.length > 0 && (
                <CompetenciesAssessmentsResponsesPlaceholder
                  isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
                  scoreScale={scoreScale}
                  sectionHeaderAddon={
                    currentCompetencySectionId
                      ? renderSectionHeader(currentCompetencySectionId)
                      : undefined
                  }
                />
              )}
              <QuestionnaireResponse
                readonly={readonly}
                content={employeeScoreContent}
                onAnswer={() => ({})}
                emptyState={<></>}
                renderSectionHeaderAddon={renderSectionHeader}
              />
              <QuestionnaireResponse
                readonly={readonly}
                content={employeePotentialScoreContent}
                onAnswer={() => ({})}
                emptyState={<></>}
                renderSectionFooter={() => (
                  <BoxedCallout
                    description={i18n.t(
                      'performance_process.employee_potential_score.questionnaire.callout'
                    )}
                  />
                )}
              />
            </Box>
          </Tabs.Tab>
        ))}
      </Tabs>
    </Box>
  )
}
